/**
 * HELPER TO HANDLE THE COLUMNS TABLES FOR THE ANALYTICS / JOURNEY
 */

import { renderToStaticMarkup } from 'react-dom/server';
import React from 'react';
import * as moment from 'moment';
import i18n from '../../../services/i18n';
import UserColumn from '../../../commonComponents/lists/columns/UserColumn';
import {
  displayUserName,
  formatDate,
  formatNumbers,
  JOURNEY_STATUS,
  JOURNEY_STEP_TYPE,
  newFormatPrice,
  TROPHY_CONFIG,
} from '../../../services/utilityFunctions';

const getUnity = (unity, t) => {
  let infoUnity = '';
  if (unity === 'points') {
    infoUnity = 'pts';
  } else if (unity === 'percentage') {
    infoUnity = '%';
  } else if (unity === 'VA' || unity === 'va') {
    infoUnity = t('VA');
  }
  return infoUnity;
};
const JOURNEY_REPORT_STEP_STATUS = {
  DONE: 'done',
  NOT_DONE: 'not_done',
};
// BEGIN: FORMAT COLUMNS
const renderCompletedDate = (lastUpdate, status = null) => {
  if (lastUpdate && status && status === JOURNEY_STATUS.FINISHED) {
    return formatDate(lastUpdate, 'DD MMM, YYYY');
  }
  if (lastUpdate) {
    return formatDate(lastUpdate, 'DD MMM, YYYY');
  }
  return '-';
};

const renderStepStatus = status => {
  const r = {
    [JOURNEY_REPORT_STEP_STATUS.NOT_DONE]: { classTitle: 'm--font-danger' },
    [JOURNEY_REPORT_STEP_STATUS.DONE]: { classTitle: 'm--font-primary' },
  };
  if (!status) {
    return '-';
  }
  return renderToStaticMarkup(
    <div>
      <span className={`m--font-bolder ${r[status]?.classTitle || ''}`}>
        {i18n.__(`reports.journey.status.step.${status}`)}
      </span>
    </div>,
  );
};

const renderStatus = status => {
  const r = {
    [JOURNEY_STATUS.UN_STARTED]: { classTitle: 'm--font-danger' },
    [JOURNEY_STATUS.IN_PROGRESS]: { classTitle: 'm--font-info' },
    [JOURNEY_STATUS.FINISHED]: { classTitle: 'm--font-primary' },
    [JOURNEY_STATUS.APPROVED]: { classTitle: 'm--font-primary' },
    [JOURNEY_STATUS.PENDING]: { classTitle: 'm--font-info' },
    [JOURNEY_STATUS.REJECTED]: { classTitle: 'm--font-danger' },
  };
  if (!status) {
    return '-';
  }
  return renderToStaticMarkup(
    <div>
      <span className={`m--font-bolder ${r[status].classTitle}`}>
        {i18n.__(`reports.journey.status.${status}`)}
      </span>
    </div>,
  );
};

const renderOrder = status => {
  const r = {
    pending_payment: {
      title: i18n.__('order_status_pending_payment'),
      class: 'status-pending_payment',
    },
    pending: {
      title: i18n.__('order_status_pending'),
      class: 'status-pending',
    },
    processing: {
      title: i18n.__('order_status_processing'),
      class: 'status-info',
    },
    processing_error: {
      title: i18n.__('order_status_processing_error'),
      class: 'status-processing_error',
    },
    completed: {
      title: i18n.__('order_status_completed'),
      class: 'status-completed',
    },
    canceled: {
      title: i18n.__('order_status_canceled'),
      class: 'status-canceled',
    },
    refunded: {
      title: i18n.__('order_status_refunded'),
      class: 'status-refunded',
    },
    failed: { title: i18n.__('order_status_failed'), class: 'status-failed' },
    lost: { title: i18n.__('order_status_lost'), class: 'status-lost' },
  };
  if (!status) {
    return '-';
  }
  return renderToStaticMarkup(
    <div>
      <span
        className={`m--font-bolder ${r[status]?.class ? r[status]?.class : ''}`}
      >
        {r[status]?.title || '-'}
      </span>
    </div>,
  );
};

const renderScoreValue = (score, unity = null, currencyCode = null) => {
  if (score === null || score === undefined) {
    return '-';
  }
  if (unity) {
    const infoUnity = getUnity(unity, i18n.__);
    return unity === 'currency'
      ? newFormatPrice(score, false, currencyCode || 'eur')
      : `${formatNumbers(score)} ${infoUnity}`;
  }
  return formatNumbers(score, false, false);
};

const renderTotalTime = totalTime => {
  if (!totalTime) {
    return '-';
  }
  const duration = moment.duration(totalTime);
  let durationAsString = '';
  if (duration.hours() > 0) {
    durationAsString += `${duration.hours()} h,`;
  }
  if (duration.minutes() > 0) {
    durationAsString += `${duration.minutes()} m,`;
  }
  durationAsString += `${duration.seconds()} s`;
  return renderToStaticMarkup(<div>{durationAsString}</div>);
};

const renderType = type =>
  renderToStaticMarkup(<span>{i18n.__(`reports.journeys.type.${type}`)}</span>);

const renderStepName = (step, trophies) => {
  switch (step.type) {
    case JOURNEY_STEP_TYPE.COURSE:
      return step.course && step.course.name ? step.course.name : '-';
    case JOURNEY_STEP_TYPE.GAME:
      return step.game && step.game.name ? step.game.name : '-';
    case JOURNEY_STEP_TYPE.SURVEY:
      return step.survey && step.survey.title ? step.survey.title : '-';
    case JOURNEY_STEP_TYPE.TROPHY:
      return step.trophy !== 'custom'
        ? i18n.__(TROPHY_CONFIG[step.trophy]?.translationKey) || '-'
        : step?.trophyCustom?.name || '-';
    default:
      return step.title || '-';
  }
};
// END: FORMAT COLUMNS

// COLUMNS
/** DYNAMIC COLORS */
const getDynamicColumns = (defaultColumns, type, showTrophy = false) => {
  const helperColumns = defaultColumns;

  switch (type) {
    case JOURNEY_STEP_TYPE.GAME:
      helperColumns.push(
        {
          field: 'score',
          title: i18n.__('reports.journey.label.score.game'),
          textAlign: 'center',
          sortable: false,
          template: ({ score, unit, currencyCode }) =>
            renderScoreValue(score, unit, currencyCode),
        },
        {
          field: 'progress',
          title: i18n.__('reports.journey.label.progress.game'),
          textAlign: 'center',
          sortable: false,
          template: ({ progress }) =>
            renderToStaticMarkup(
              <div>{progress ? `${progress.toFixed(2)}%` : '-'}</div>,
            ),
        },
        {
          field: 'date',
          title: i18n.__('reports.journey.label.date.game'),
          textAlign: 'center',
          sortable: false,
          template: ({ lastUpdate }) => renderCompletedDate(lastUpdate),
        },
      );
      break;
    case JOURNEY_STEP_TYPE.COURSE:
      helperColumns.push(
        {
          field: 'status',
          title: i18n.__('reports.journey.label.status.course'),
          textAlign: 'center',
          sortable: false,
          template: ({ status }) => renderStatus(status),
        },
        {
          field: 'score',
          title: i18n.__('reports.journey.label.score.course'),
          textAlign: 'center',
          sortable: false,
          template: ({ score }) => renderScoreValue(score),
        },
        {
          field: 'totalTime',
          title: i18n.__('reports.journey.label.time.course'),
          textAlign: 'center',
          sortable: false,
          template: ({ totalTime }) => renderTotalTime(totalTime),
        },
        {
          field: 'lastUpdate',
          title: i18n.__('reports.journey.label.date.course'),
          textAlign: 'center',
          sortable: false,
          template: ({ lastUpdate, status }) =>
            renderCompletedDate(lastUpdate, status),
        },
      );
      break;
    case JOURNEY_STEP_TYPE.SURVEY:
      helperColumns.push(
        {
          field: 'status',
          title: i18n.__('reports.journey.label.status.survey'),
          textAlign: 'center',
          sortable: false,
          template: ({ status }) => renderStatus(status),
        },
        {
          field: 'date',
          title: i18n.__('reports.journey.label.date.survey'),
          textAlign: 'center',
          sortable: false,
          template: ({ lastUpdate }) => renderCompletedDate(lastUpdate),
        },
      );
      break;
    case JOURNEY_STEP_TYPE.TASK:
    case JOURNEY_STEP_TYPE.SIGNATURE:
    case JOURNEY_STEP_TYPE.DOCUMENT:
    case JOURNEY_STEP_TYPE.FILL_AND_SIGN:
      helperColumns.push(
        {
          field: 'status',
          title: i18n.__(`reports.journey.label.status.${type}`),
          textAlign: 'center',
          sortable: false,
          template: ({ status }) => renderStatus(status),
        },
        {
          field: 'date',
          title: i18n.__(`reports.journey.label.date.${type}`),
          textAlign: 'center',
          sortable: false,
          template: ({ lastUpdate }) => renderCompletedDate(lastUpdate),
        },
      );
      break;
    case JOURNEY_STEP_TYPE.TROPHY:
      if (showTrophy) {
        helperColumns.push({
          field: 'trophy',
          title: '',
          textAlign: 'center',
          sortable: false,
          template: () =>
            renderToStaticMarkup(
              <span>{i18n.__('reports.journeys.table.trophy')}</span>,
            ),
        });
      }
      break;
    case JOURNEY_STEP_TYPE.MARKET_PRODUCT_PURCHASE:
      helperColumns.push(
        {
          field: 'orderNumber',
          title: i18n.__('order.number'),
          textAlign: 'center',
          sortable: false,
          template: ({ orderNumber }) =>
            renderToStaticMarkup(<span>{orderNumber || '-'}</span>),
        },
        {
          field: 'orderStatus',
          title: i18n.__('order.status'),
          textAlign: 'center',
          sortable: false,
          template: ({ status }) => renderOrder(status),
        },
        {
          field: 'orderTotal',
          title: i18n.__('order.total'),
          textAlign: 'center',
          sortable: false,
          template: ({ orderTotal, orderCurrency }) =>
            renderToStaticMarkup(
              <span>
                {orderTotal
                  ? newFormatPrice(orderTotal, true, orderCurrency)
                  : '-'}
              </span>,
            ),
        },
        {
          field: 'orderDate',
          title: i18n.__('order.date'),
          textAlign: 'center',
          sortable: false,
          template: ({ lastUpdate }) => renderCompletedDate(lastUpdate),
        },
      );
      break;
    default:
      helperColumns.push({
        field: 'progress',
        title: i18n.__('reports.journey.label.progress.profile'),
        textAlign: 'center',
        sortable: false,
        template: row =>
          renderToStaticMarkup(
            <div>{row.progress ? `${row.progress.toFixed(0)}%` : '-'}</div>,
          ),
      });
      break;
  }

  return helperColumns;
};

/** USE CASE: Journey & NO User (sub table) */
const getColumnJourneyNoUser = data => {
  const { type = null } = data || {};
  let typeFromColumn = null;

  const defaultColumns = [
    {
      field: 'username',
      title: i18n.__('reports.journeys.table.user'),
      textAlign: 'left',
      width: 158,
      template: ({ owner, firstName, lastName, image, username }) =>
        renderToStaticMarkup(
          <UserColumn
            t={i18n.__}
            smallFormat
            includeInvitedStatus
            user={{
              _id: owner,
              firstName,
              lastName,
              image,
              username:
                firstName || lastName || username
                  ? displayUserName({ firstName, lastName, username })
                  : `(${i18n.__('reports.marketplace.orders.user.removed')})`,
            }}
          />,
        ),
    },
    {
      field: 'stepStatus',
      title: i18n.__('reports.scorm.table.stepStatus'),
      textAlign: 'center',
      template: ({ stepStatus, type: typeProp }) => {
        if (typeProp) {
          typeFromColumn = typeProp;
        }
        if (stepStatus) {
          return renderStepStatus(stepStatus);
        }
        return '-';
      },
    },
    {
      field: 'stepStatusDate',
      title: i18n.__('reports.scorm.table.stepStatusDate'),
      textAlign: 'center',
      template: item => {
        if (item.stepCompletedAt) {
          return formatDate(item.stepCompletedAt, 'DD MMM, YYYY');
        }
        return '-';
      },
    },
  ];
  return getDynamicColumns(defaultColumns, type || typeFromColumn);
};
// ROW CREATION
const subTableJourney = (row, getRowParams) => {
  $('<div/>')
    .attr('id', `child_data_local_${row.data._id}`)
    .appendTo(row.detailCell)
    .mDatatable({
      data: {
        type: 'remote',
        source: {
          read: {
            url: `/table/analytics/journey/step/${row.data._id}`,
            method: 'GET',
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
            },
            params: getRowParams(),
          },
        },
        pageSize: 10,
        serverPaging: true,
        serverFiltering: true,
        serverSorting: true,
        saveState: {
          cookie: false,
          webstorage: false,
        },
      },
      layout: {
        theme: 'default',
        scroll: true,
        header: true,
        footer: false,
        spinner: {
          type: 1,
          theme: 'default',
        },
      },
      sortable: true,
      columns: getColumnJourneyNoUser(row.data),
    });
};

/** USE CASE: Journey & User (sub table) */
const getColumnJourneyWithUser = trophies => [
  {
    field: '_id',
    title: '',
    textAlign: 'left',
    width: 50,
  },
  {
    field: 'step',
    title: i18n.__('reports.journeys.table.step'),
    textAlign: 'left',
    template: ({ pos }) => {
      if (pos >= 0) {
        return pos + 1;
      }
      return '-';
    },
  },
  {
    field: 'type',
    title: i18n.__('reports.journeys.table.type'),
    textAlign: 'left',
    template: ({ type }) => renderType(type),
  },
  {
    field: 'title',
    title: i18n.__('reports.journeys.table.name'),
    textAlign: 'left',
    template: step => renderStepName(step, trophies),
  },
  {
    field: 'stepStatus',
    title: i18n.__('reports.scorm.table.stepStatus'),
    textAlign: 'left',
    template: ({ userStatus }) => {
      if (userStatus && userStatus.stepStatus) {
        return renderStepStatus(userStatus.stepStatus);
      }
      return '-';
    },
  },
  {
    field: 'stepStatusDate',
    title: i18n.__('reports.scorm.table.stepStatusDate'),
    textAlign: 'left',
    template: ({ userStatus }) => {
      if (userStatus && userStatus.stepCompletedAt) {
        return formatDate(userStatus.stepCompletedAt, 'DD MMM, YYYY');
      }
      return '-';
    },
  },
];
// ROW CREATION
const subTableJourneyUser = row => {
  $('<div/>')
    .attr('id', `child_data_local_${row.data._id}`)
    .appendTo(row.detailCell)
    .mDatatable({
      data: {
        type: 'local',
        source: [row.data.userStatus],
        pageSize: 1,
      },
      layout: {
        theme: 'default',
        minHeight: 94,
        height: 94,
        scroll: true,
        header: true,
        footer: false,
        spinner: {
          type: 1,
          theme: 'default',
        },
      },
      pagination: false,
      sortable: true,
      columns: getDynamicColumns([], row.data.type, true),
    });
};

/** USE CASE: NO Journey & User */
const getNoJourneyWithUser = trophies => [
  {
    field: 'step',
    title: i18n.__('reports.journeys.table.step'),
    textAlign: 'left',
    template: ({ pos }) => {
      if (pos >= 0) {
        return pos + 1;
      }
      return '-';
    },
  },
  {
    field: 'type',
    title: i18n.__('reports.journeys.table.type'),
    textAlign: 'left',
    template: ({ type }) => renderType(type),
  },
  {
    field: 'title',
    title: i18n.__('reports.journeys.table.name'),
    textAlign: 'left',
    template: step => renderStepName(step, trophies),
  },
  {
    field: 'stepStatus',
    title: i18n.__('reports.scorm.table.stepStatus'),
    textAlign: 'left',
    template: ({ userStatus }) => {
      if (userStatus && userStatus.stepStatus) {
        return renderStepStatus(userStatus.stepStatus);
      }
      return '-';
    },
  },
  {
    field: 'stepStatusDate',
    title: i18n.__('reports.scorm.table.stepStatusDate'),
    textAlign: 'left',
    template: ({ userStatus }) => {
      if (userStatus && userStatus.stepCompletedAt) {
        return formatDate(userStatus.stepCompletedAt, 'DD MMM, YYYY');
      }
      return '-';
    },
  },
];
// ROW CREATION
const subTableUser = (row, filters, getRowParams, trophies) => {
  $('<div/>')
    .attr('id', `child_data_local_${row.data._id}`)
    .appendTo(row.detailCell)
    .mDatatable({
      data: {
        type: 'remote',
        source: {
          read: {
            url: `/table/analytics/journey/user/${filters.user}/journey/${row.data._id}`,
            method: 'GET',
            headers: {
              'Accept-Version': '1.0.0',
              'X-Gamification-API': 'Games',
            },
            params: getRowParams(),
          },
        },
        pageSize: 10,
        serverPaging: true,
        serverFiltering: true,
        serverSorting: true,
        saveState: {
          cookie: false,
          webstorage: false,
        },
      },
      layout: {
        theme: 'default',
        scroll: true,
        header: true,
        footer: false,
        spinner: {
          type: 1,
          theme: 'default',
        },
      },
      sortable: false,
      columns: getNoJourneyWithUser(trophies),
    });
};

/** MAIN TABLES */
const getJourneyReportColumns = (journeyId, userId, trophies) => {
  if (journeyId && !userId) {
    return [
      {
        field: '_id',
        title: '',
        sortable: !1,
        width: 10,
        textAlign: 'center',
        template: step => renderToStaticMarkup(<div>{step._id}</div>),
      },
      {
        field: 'row',
        title: i18n.__('reports.journeys.table.step'),
        sortable: !1,
        width: 150,
        textAlign: 'center',
        template: (row, index) => renderToStaticMarkup(index + 1),
      },
      {
        field: 'type',
        title: i18n.__('reports.journeys.table.type'),
        textAlign: 'center',
        width: 150,
        template: ({ type }) => renderType(type),
      },
      {
        field: 'title',
        title: i18n.__('reports.journeys.table.name'),
        textAlign: 'center',
        width: 150,
        template: step => renderStepName(step, trophies),
      },
      {
        field: 'totalActiveUsers',
        title: i18n.__('reports.journeys.finished.users'),
        textAlign: 'center',
        width: 100,
        template: ({ totalFinishedUsers, totalAffectedUsers }) =>
          renderToStaticMarkup(
            <div>
              {`${totalFinishedUsers >= 0 ? totalFinishedUsers : '-'}/${
                totalAffectedUsers >= 0 ? totalAffectedUsers : '-'
              }`}
            </div>,
          ),
      },
    ];
  }
  if (journeyId && userId) {
    return getColumnJourneyWithUser(trophies);
  }
  if (!journeyId && userId) {
    return [
      {
        field: '_id',
        title: '',
        sortable: !1,
        width: 10,
        textAlign: 'center',
        template: user => renderToStaticMarkup(<div>{user._id}</div>),
      },
      {
        field: 'title',
        title: i18n.__('reports.journeys.table.journey'),
        textAlign: 'center',
        width: 150,
        template: step => renderToStaticMarkup(<div>{step.title}</div>),
      },
      {
        field: 'totalSteps',
        title: i18n.__('reports.journeys.total.steps'),
        textAlign: 'center',
        width: 150,
        template: ({ totalSteps }) =>
          renderToStaticMarkup(<div>{totalSteps >= 0 ? totalSteps : '-'}</div>),
      },
      {
        field: 'userStepsDone',
        title: i18n.__('reports.journeys.table.stepsMade'),
        textAlign: 'center',
        width: 150,
        template: ({ userStepsDone }) =>
          renderToStaticMarkup(
            <div>{userStepsDone >= 0 ? userStepsDone : '-'}</div>,
          ),
      },
    ];
  }
  return [];
};

export {
  getDynamicColumns,
  getColumnJourneyNoUser,
  getColumnJourneyWithUser,
  getNoJourneyWithUser,
  subTableJourney,
  subTableUser,
  subTableJourneyUser,
  getJourneyReportColumns,
  renderStepName,
};
